(() => {
  const THEMES = ['light', 'dark', 'accent'];
  const STORAGE_KEY = 'db-theme';
  const COOKIE_KEY = 'db_theme';

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value) {
    const maxAge = 60 * 60 * 24 * 365;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  function normalizeTheme(value) {
    return THEMES.includes(value) ? value : 'dark';
  }

  function getPreferredTheme() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) return normalizeTheme(stored);
    } catch (e) {
      /* ignore */
    }
    const cookieTheme = readCookie(COOKIE_KEY);
    if (cookieTheme) return normalizeTheme(cookieTheme);
    return 'dark';
  }

  function applyTheme(theme, { persist = true } = {}) {
    const next = normalizeTheme(theme);
    document.documentElement.setAttribute('data-theme', next);
    document.documentElement.style.colorScheme = next === 'light' ? 'light' : 'dark';

    document.querySelectorAll('[data-theme-switcher]').forEach((group) => {
      group.querySelectorAll('[data-theme-value]').forEach((btn) => {
        const active = btn.getAttribute('data-theme-value') === next;
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
        btn.classList.toggle('is-active', active);
      });
    });

    if (persist) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (e) {
        /* ignore */
      }
      writeCookie(COOKIE_KEY, next);
    }
  }

  function bindThemeSwitchers() {
    document.querySelectorAll('[data-theme-switcher]').forEach((group) => {
      group.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-theme-value]');
        if (!btn || !group.contains(btn)) return;
        applyTheme(btn.getAttribute('data-theme-value'));
      });

      group.addEventListener('keydown', (event) => {
        const buttons = Array.from(group.querySelectorAll('[data-theme-value]'));
        const currentIndex = buttons.findIndex((btn) => btn.getAttribute('aria-checked') === 'true');
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          const next = buttons[(currentIndex + 1) % buttons.length];
          next.focus();
          applyTheme(next.getAttribute('data-theme-value'));
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          const next = buttons[(currentIndex - 1 + buttons.length) % buttons.length];
          next.focus();
          applyTheme(next.getAttribute('data-theme-value'));
        }
      });
    });
  }

  function bindHeaderDrawer() {
    const root = document.querySelector('[data-db-header]');
    if (!root) return;
    const toggle = root.querySelector('[data-db-menu-toggle]');
    const drawer = root.querySelector('[data-db-drawer]');
    const closeButtons = root.querySelectorAll('[data-db-drawer-close]');
    if (!toggle || !drawer) return;

    const setOpen = (open) => {
      drawer.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', () => setOpen(!drawer.classList.contains('is-open')));
    closeButtons.forEach((btn) => btn.addEventListener('click', () => setOpen(false)));
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
  }

  function bindA11yWidget() {
    const root = document.querySelector('[data-db-a11y]');
    if (!root) return;
    const toggle = root.querySelector('[data-db-a11y-toggle]');
    const panel = root.querySelector('[data-db-a11y-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const open = root.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    panel.querySelectorAll('[data-a11y-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.getAttribute('data-a11y-action');
        if (action === 'large-text') document.documentElement.classList.toggle('db-a11y-large-text');
        if (action === 'high-contrast') document.documentElement.classList.toggle('db-a11y-high-contrast');
        if (action === 'underline-links') document.documentElement.classList.toggle('db-a11y-underline-links');
        if (action === 'reset') {
          document.documentElement.classList.remove('db-a11y-large-text', 'db-a11y-high-contrast', 'db-a11y-underline-links');
        }
      });
    });
  }

  function bindReveals() {
    const nodes = document.querySelectorAll('.db-reveal, .db-stagger');
    if (!nodes.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach((node) => observer.observe(node));
  }

  function boot() {
    applyTheme(getPreferredTheme(), { persist: true });
    bindThemeSwitchers();
    bindHeaderDrawer();
    bindA11yWidget();
    bindReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.DoBetterTheme = { applyTheme, getPreferredTheme, THEMES };
})();
